import type {
  ExpertBalanceSheetAccountComponent,
  ExpertBalanceSheetColumnValues,
  ExpertBalanceSheetComponent,
  ExpertBalanceSheetGroupComponent,
  ExpertBalanceSheetReport,
  ExpertBalanceSheetSectionComponent,
  ExpertBalanceSheetSumComponent,
} from '../types/expertBalanceSheetReport'
import type {
  ExpertBalanceSheetAssetsViewData,
  ExpertBalanceSheetControl,
  ExpertBalanceSheetLiabilitiesViewData,
  ExpertBalanceSheetPeriodColumn,
  ExpertBalanceSheetRowKind,
} from '../types/expertBalanceSheetView'
import { ExpertBalanceSheetViewMode } from '../types/expertBalanceSheetView'

const ASSETS_COMPONENT_INDEX = 0
const LIABILITIES_COMPONENT_INDEX = 1

const IMBALANCE_TOLERANCE = 0.005

/**
 * The API serves raw account balances: Actif is debit-normal and arrives positive, Passif is
 * credit-normal and arrives negative. A Bilan reads both sides positive, so the Passif side is
 * flipped on the way in, as the P&L view does for its revenue rows.
 */
type Orientation = 1 | -1

const ASSETS_ORIENTATION: Orientation = 1
const LIABILITIES_ORIENTATION: Orientation = -1

const EMPTY_ASSETS_VIEW_DATA: ExpertBalanceSheetAssetsViewData = { periods: [], rows: [] }
const EMPTY_LIABILITIES_VIEW_DATA: ExpertBalanceSheetLiabilitiesViewData = { periods: [], rows: [] }

interface FlatComponentRow {
  id: string
  label: string
  kind: ExpertBalanceSheetRowKind
  accountNumber?: string
  columnValues: ExpertBalanceSheetColumnValues
}

const getRootSection = (
  report: ExpertBalanceSheetReport | undefined,
  index: number,
): ExpertBalanceSheetSectionComponent | undefined => {
  const component = report?.components[index]

  return component?.type === 'section' ? component : undefined
}

const getRowKind = (
  component: ExpertBalanceSheetGroupComponent | ExpertBalanceSheetSumComponent,
  parent: ExpertBalanceSheetSectionComponent,
  rootSection: ExpertBalanceSheetSectionComponent,
): ExpertBalanceSheetRowKind => {
  if (component.type === 'group') {
    return 'line'
  }

  return parent === rootSection ? 'total' : 'subtotal'
}

/** The BE serializes an empty `children` map as `[]`, and a populated one as an object keyed by account id. */
const getGroupAccounts = (group: ExpertBalanceSheetGroupComponent): ExpertBalanceSheetAccountComponent[] =>
  Array.isArray(group.children) ? group.children : Object.values(group.children)

const mapAccountRows = (group: ExpertBalanceSheetGroupComponent, groupPath: string): FlatComponentRow[] =>
  getGroupAccounts(group).map((account) => ({
    accountNumber: account.accountNumber,
    columnValues: account.column_values,
    id: `${groupPath}/${account.id}`,
    kind: 'account',
    label: account.label,
  }))

const flattenRootSection = (
  rootSection: ExpertBalanceSheetSectionComponent,
  path: string,
  viewMode: ExpertBalanceSheetViewMode,
): FlatComponentRow[] => {
  const rows: FlatComponentRow[] = []
  const includeAccounts = viewMode === ExpertBalanceSheetViewMode.Detailed

  const walk = (
    components: ExpertBalanceSheetComponent[],
    parent: ExpertBalanceSheetSectionComponent,
    parentPath: string,
  ): void => {
    for (const [index, component] of components.entries()) {
      const componentPath = `${parentPath}/${index}`

      switch (component.type) {
        case 'section':
          walk(component.children, component, componentPath)
          break
        case 'account':
          break
        case 'group':
        case 'sum':
          rows.push({
            columnValues: component.column_values,
            id: componentPath,
            kind: getRowKind(component, parent, rootSection),
            label: component.label,
          })

          if (includeAccounts && component.type === 'group') {
            rows.push(...mapAccountRows(component, componentPath))
          }
          break
        default:
          ;(component) satisfies never
          break
      }
    }
  }

  walk(rootSection.children, rootSection, path)

  return rows
}

const mapPeriods = (report: ExpertBalanceSheetReport): ExpertBalanceSheetPeriodColumn[] =>
  report.header.map((periodHeader) => ({
    endDate: periodHeader.end_date,
    key: periodHeader.key,
    label: periodHeader.label,
    startDate: periodHeader.start_date,
  }))

const applyOrientation = (value: number | undefined, orientation: Orientation): number | undefined => {
  if (value === undefined || value === 0) {
    return value
  }

  return orientation * value
}

const mapNetForPeriods = (
  columnValues: ExpertBalanceSheetColumnValues,
  periods: ExpertBalanceSheetPeriodColumn[],
  orientation: Orientation,
): (number | undefined)[] =>
  periods.map((period) => {
    const metrics = columnValues[period.key]

    return applyOrientation(metrics?.net ?? metrics?.value, orientation)
  })

const mapMetricForPeriods = (
  columnValues: ExpertBalanceSheetColumnValues,
  periods: ExpertBalanceSheetPeriodColumn[],
  orientation: Orientation,
  metric: 'gross' | 'depreciation',
): (number | undefined)[] => periods.map((period) => applyOrientation(columnValues[period.key]?.[metric], orientation))

export const selectBalanceSheetAssetsView = (
  report: ExpertBalanceSheetReport | undefined,
  viewMode: ExpertBalanceSheetViewMode = ExpertBalanceSheetViewMode.Summary,
): ExpertBalanceSheetAssetsViewData => {
  const rootSection = getRootSection(report, ASSETS_COMPONENT_INDEX)

  if (!report || !rootSection) {
    return EMPTY_ASSETS_VIEW_DATA
  }

  const periods = mapPeriods(report)

  return {
    periods,
    rows: flattenRootSection(rootSection, 'assets', viewMode).map((row) => ({
      accountNumber: row.accountNumber,
      depreciation: mapMetricForPeriods(row.columnValues, periods, ASSETS_ORIENTATION, 'depreciation'),
      gross: mapMetricForPeriods(row.columnValues, periods, ASSETS_ORIENTATION, 'gross'),
      id: row.id,
      kind: row.kind,
      label: row.label,
      net: mapNetForPeriods(row.columnValues, periods, ASSETS_ORIENTATION),
    })),
  }
}

export const selectBalanceSheetLiabilitiesView = (
  report: ExpertBalanceSheetReport | undefined,
  viewMode: ExpertBalanceSheetViewMode = ExpertBalanceSheetViewMode.Summary,
): ExpertBalanceSheetLiabilitiesViewData => {
  const rootSection = getRootSection(report, LIABILITIES_COMPONENT_INDEX)

  if (!report || !rootSection) {
    return EMPTY_LIABILITIES_VIEW_DATA
  }

  const periods = mapPeriods(report)

  return {
    periods,
    rows: flattenRootSection(rootSection, 'liabilitiesAndEquity', viewMode).map((row) => ({
      accountNumber: row.accountNumber,
      id: row.id,
      kind: row.kind,
      label: row.label,
      net: mapNetForPeriods(row.columnValues, periods, LIABILITIES_ORIENTATION),
    })),
  }
}

const getGrandTotal = (rows: { kind: ExpertBalanceSheetRowKind; net: (number | undefined)[] }[]): number | undefined =>
  rows.find((row) => row.kind === 'total')?.net[0]

export const selectBalanceSheetControl = (
  assetsView: ExpertBalanceSheetAssetsViewData,
  liabilitiesView: ExpertBalanceSheetLiabilitiesViewData,
): ExpertBalanceSheetControl | null => {
  const assetsTotal = getGrandTotal(assetsView.rows)
  const liabilitiesTotal = getGrandTotal(liabilitiesView.rows)

  if (assetsTotal === undefined || liabilitiesTotal === undefined) {
    return null
  }

  const imbalance = assetsTotal - liabilitiesTotal

  return { imbalance, isBalanced: Math.abs(imbalance) < IMBALANCE_TOLERANCE }
}

export const isBalanceSheetReportEmpty = (
  assetsView: ExpertBalanceSheetAssetsViewData,
  liabilitiesView: ExpertBalanceSheetLiabilitiesViewData,
): boolean => {
  const rows = [...assetsView.rows, ...liabilitiesView.rows]

  return rows.every((row) => row.net.every((value) => !value))
}
